/**
 * @cfleet/auth - ProtectedRoute Component
 *
 * A route protection component for web applications using react-router-dom.
 * Wraps protected routes and handles authentication/authorization checks.
 *
 * Features:
 * - Redirects unauthenticated users to login
 * - Supports role-based access control (OR logic)
 * - Supports permission-based access control (AND/OR configurable)
 * - Customizable loading and unauthorized states
 * - Preserves intended destination for post-login redirect
 *
 * @example
 * ```tsx
 * // Basic protection - just require authentication
 * <Route
 *   path="/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <DashboardPage />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * // Role-based protection
 * <Route
 *   path="/admin/*"
 *   element={
 *     <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
 *       <AdminSection />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * // Permission-based protection
 * <Route
 *   path="/drivers"
 *   element={
 *     <ProtectedRoute requiredPermissions={['manageDrivers']}>
 *       <DriversPage />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * // Combined with custom components
 * <ProtectedRoute
 *   requiredRoles={['Admin']}
 *   loadingComponent={<CustomSpinner />}
 *   unauthorizedComponent={<Custom403Page />}
 * >
 *   <AdminPage />
 * </ProtectedRoute>
 * ```
 */

import { type ReactNode, type CSSProperties } from 'react';
import { useProtectedRoute, type UseProtectedRouteOptions } from '../hooks/useProtectedRoute';

/**
 * Props for the ProtectedRoute component
 */
export interface ProtectedRouteProps extends UseProtectedRouteOptions {
  /** The content to render when authorized */
  children: ReactNode;

  /**
   * Path to redirect to when not authenticated
   * @default '/login'
   */
  loginPath?: string;

  /**
   * Path to redirect to when authenticated but not authorized.
   * If not provided, unauthorizedComponent will be shown instead.
   */
  unauthorizedPath?: string;

  /**
   * Custom component to show while checking authentication
   * @default Built-in loading spinner
   */
  loadingComponent?: ReactNode;

  /**
   * Custom component to show when user is not authorized (wrong role/permission)
   * Only used if unauthorizedPath is not provided.
   * @default Built-in "Access Denied" message
   */
  unauthorizedComponent?: ReactNode;

  /**
   * Callback fired when user is redirected to login
   * Useful for analytics or cleanup
   */
  onUnauthenticated?: () => void;

  /**
   * Callback fired when user is denied access due to authorization
   * Useful for analytics or logging
   */
  onUnauthorized?: () => void;
}

/**
 * Default loading component styles
 */
const defaultLoadingStyles: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100%',
};

const spinnerStyles: CSSProperties = {
  width: '40px',
  height: '40px',
  border: '3px solid #e5e7eb',
  borderTopColor: '#3b82f6',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

/**
 * Default loading component
 */
function DefaultLoading() {
  return (
    <div style={defaultLoadingStyles} data-testid="protected-route-loading">
      <div style={spinnerStyles} aria-label="Loading" role="status">
        <span style={{ position: 'absolute', clip: 'rect(0, 0, 0, 0)' }}>Loading...</span>
      </div>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

/**
 * Default unauthorized component styles
 */
const defaultUnauthorizedStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100%',
  padding: '24px',
  textAlign: 'center',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const unauthorizedIconStyles: CSSProperties = {
  fontSize: '64px',
  marginBottom: '16px',
};

const unauthorizedTitleStyles: CSSProperties = {
  fontSize: '24px',
  fontWeight: '600',
  color: '#111827',
  margin: '0 0 8px 0',
};

const unauthorizedMessageStyles: CSSProperties = {
  fontSize: '16px',
  color: '#6b7280',
  margin: '0 0 24px 0',
  maxWidth: '400px',
};

const backButtonStyles: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  textDecoration: 'none',
};

/**
 * Default unauthorized component
 */
function DefaultUnauthorized() {
  const handleGoBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    }
  };

  return (
    <div style={defaultUnauthorizedStyles} data-testid="protected-route-unauthorized">
      <div style={unauthorizedIconStyles} role="img" aria-label="Access denied">
        🚫
      </div>
      <h1 style={unauthorizedTitleStyles}>Access Denied</h1>
      <p style={unauthorizedMessageStyles}>
        You do not have permission to access this page. Please contact your administrator if you
        believe this is an error.
      </p>
      <button style={backButtonStyles} onClick={handleGoBack} type="button">
        Go Back
      </button>
    </div>
  );
}

/**
 * Navigate component that works without react-router-dom dependency at import time.
 * Uses window.location for navigation as a fallback.
 *
 * If react-router-dom is available, it will use that for navigation.
 * This allows the component to work both with and without the router.
 */
function NavigateToPath({
  to,
  state,
  replace = true,
}: {
  to: string;
  state?: unknown;
  replace?: boolean;
}) {
  // Try to use react-router-dom if available
  try {
    // Dynamic import to avoid hard dependency
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { Navigate, useLocation } = require('react-router-dom');

    // Get current location to preserve intended destination
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const location = useLocation();

    return <Navigate to={to} state={state ?? { from: location }} replace={replace} />;
  } catch {
    // Fallback to window.location if react-router-dom is not available
    if (typeof window !== 'undefined' && window.location) {
      try {
        if (replace && typeof window.location.replace === 'function') {
          window.location.replace(to);
        } else if (typeof window.location.assign === 'function') {
          window.location.assign(to);
        } else {
          // Final fallback - direct href assignment
          window.location.href = to;
        }
      } catch {
        // In test environments (jsdom), location methods may not work
        // Just return null and let the test handle the redirect verification
        console.debug('[ProtectedRoute] Navigation fallback to:', to);
      }
    }
    return null;
  }
}

/**
 * ProtectedRoute component for web applications.
 *
 * Protects routes by checking authentication and authorization status.
 * Handles loading states, redirects, and access denial gracefully.
 *
 * @param props - Component props including children and protection options
 * @returns Protected content, loading state, unauthorized message, or redirect
 */
export function ProtectedRoute({
  children,
  loginPath = '/login',
  unauthorizedPath,
  loadingComponent,
  unauthorizedComponent,
  onUnauthenticated,
  onUnauthorized,
  requiredRoles,
  requiredPermissions,
  requireAllPermissions,
}: ProtectedRouteProps): ReactNode {
  const { isLoading, authorizationError } = useProtectedRoute({
    requiredRoles,
    requiredPermissions,
    requireAllPermissions,
  });

  // Show loading state while checking authentication
  if (isLoading) {
    return loadingComponent ?? <DefaultLoading />;
  }

  // Handle unauthenticated state - redirect to login
  if (authorizationError === 'unauthenticated') {
    onUnauthenticated?.();
    return <NavigateToPath to={loginPath} replace />;
  }

  // Handle unauthorized state - show forbidden or redirect
  if (authorizationError === 'unauthorized') {
    onUnauthorized?.();

    // If unauthorizedPath is provided, redirect there
    if (unauthorizedPath) {
      return <NavigateToPath to={unauthorizedPath} replace />;
    }

    // Otherwise show the unauthorized component
    return unauthorizedComponent ?? <DefaultUnauthorized />;
  }

  // User is authorized - render children
  return <>{children}</>;
}

/**
 * Re-export types for convenience
 */
export type { UseProtectedRouteOptions } from '../hooks/useProtectedRoute';
